import { Injectable, inject } from '@angular/core';
import { ProductFeeType } from '@shared/constants';
import { addDays, getCurrentDate, getDateString } from '@shared/helpers/date-helpers';
import {
  Availability,
  LenderProductTransferViewModel,
  MatchedLenderProducts,
  MortgageType,
  Product,
  ProductTransferInfoModel,
} from '@shared/models';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductsTransferService {
  private readonly dataService = inject(ClubHubDataService);

  public getProductTransferDetails(lenderName: string): Observable<ProductTransferInfoModel> {
    return this.dataService.get<ProductTransferInfoModel>(`Product/TransferDetails/${lenderName}`);
  }

  public getProductTransferProducts(
    lendersProducts: MatchedLenderProducts[],
    currentMortgageLender: string,
    productTransferInitialDate: Date | string | undefined,
    mortgageType: MortgageType,
  ): Observable<LenderProductTransferViewModel | null> {
    if (!currentMortgageLender || mortgageType === MortgageType.Purchase) {
      return of(null);
    }

    return this.getProductTransferDetails(currentMortgageLender).pipe(
      map(transferDetails => {
        const selectedLenderProducts = this.getSelectedLenderProducts(lendersProducts, currentMortgageLender);

        if (!selectedLenderProducts?.products) {
          return {
            errorMessage: this.getNoEligibleItemsMessage(currentMortgageLender, transferDetails),
          } as LenderProductTransferViewModel;
        }

        const productTransferProducts = this.markAndGetProductTransferProducts(selectedLenderProducts.products, currentMortgageLender);

        if (productTransferProducts.length < 1) {
          return {
            errorMessage: this.getNoEligibleItemsMessage(currentMortgageLender, transferDetails),
          } as LenderProductTransferViewModel;
        }

        if (productTransferInitialDate && transferDetails?.timeBeforeApplicable) {
          productTransferInitialDate = new Date(productTransferInitialDate);

          const currentDate = getCurrentDate();
          if (productTransferInitialDate >= currentDate) {
            const availableDate = addDays(productTransferInitialDate, -transferDetails.timeBeforeApplicable);
            const isNotEligibleDate = availableDate > currentDate;
            if (isNotEligibleDate) {
              return {
                errorMessage: this.getInvalidDateErrorMessage(currentMortgageLender, availableDate),
              } as LenderProductTransferViewModel;
            }
          }
        }

        return {
          ...transferDetails,
          errorMessage: '',
          infoMessage: `Product transfers for ${currentMortgageLender} are available`,
          initialPeriodDate: productTransferInitialDate,
          lenderName: currentMortgageLender,
          products: productTransferProducts,
        } as LenderProductTransferViewModel;
      }),
      catchError(() =>
        of({
          errorMessage: this.getNoEligibleItemsMessage(currentMortgageLender, null),
        } as LenderProductTransferViewModel),
      ),
      shareReplay(1),
    );
  }

  private markAndGetProductTransferProducts(products: Product[], selectedLender: string): Product[] {
    // Mark products as productTransfer
    const productTransferProducts = products.filter(
      p =>
        (p.productTransfer === Availability.AlsoAvailable || p.productTransfer === Availability.OnlyAvailable) &&
        p.lender.toLowerCase() === selectedLender?.toLowerCase(),
    );

    /*
      Regarding to MCONE-5313 ticket we need to remove "ChapsFee", "ValuationFee"
      and "LegalFee" from productTransfer products
    */
    productTransferProducts.forEach(product => {
      product.totalFees -=
        (product.fees[ProductFeeType.Valuation] ?? 0) +
        (product.fees[ProductFeeType.Chaps] ?? 0) +
        (product.fees[ProductFeeType.Conveyancing] ?? 0);

      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete product.fees[ProductFeeType.Valuation];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete product.fees[ProductFeeType.Chaps];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete product.fees[ProductFeeType.Conveyancing];

      product.productTransferEligible = true;
      product.isValuationFeeScaled = false;
      product.freeLegal = null;
      product.freeVal = null;
    });

    return productTransferProducts;
  }

  public filterProductTransferProducts(products: Product[], selectedLender?: string): Product[] {
    if (!selectedLender) {
      return products;
    }

    this.markAndGetProductTransferProducts(products, selectedLender);

    // Exclude ProductTransfer product of another lenders
    const productsWithoutProductTransfer = products.filter(
      p =>
        p.lender.toLowerCase() === selectedLender.toLowerCase() ||
        (p.lender.toLowerCase() !== selectedLender.toLowerCase() && p.productTransfer !== Availability.OnlyAvailable),
    );

    return productsWithoutProductTransfer;
  }

  private getSelectedLenderProducts(
    lendersProducts: MatchedLenderProducts[],
    currentMortgageLender: string,
  ): MatchedLenderProducts | undefined {
    return lendersProducts.find(r => r.lenderName.toLowerCase() === currentMortgageLender.toLocaleLowerCase());
  }

  private getNoEligibleItemsMessage(lenderName: string, transferDetails: ProductTransferInfoModel | null) {
    const noProductsMessage = `Product transfers for ${lenderName} are not available`;
    if (!transferDetails?.retentionUrl) {
      return noProductsMessage;
    }

    const link = `
      <a href="${transferDetails.retentionUrl}" target="_blank">
        You can visit this link to view their product transfer process
      </a>
    `;
    return `${noProductsMessage}. ${link}`;
  }

  private getInvalidDateErrorMessage(lenderName: string, maxAvailableDate: Date) {
    return (
      'Based on the information provided, your customer is not eligible for a product transfer with ' +
      `<b>${lenderName}</b> until <b>${getDateString(maxAvailableDate)}</b>`
    );
  }
}
